'use client';

import { FC, useMemo, type ReactNode } from 'react';
import {
  getCountryName,
  getLanguageName
} from '@/services/isomorphic/I18NService';
import { Icon, IconTypes } from '@/react/components/core-ui/Icon';
import LocaleListVM from '@/react/view-models/LocaleListVM';
import type { NavigationModel } from '@/services/models/Navigation/NavigationModel';
import { ViewMoreCountries } from '@/react/components/country/ViewMoreCountries';
import { observer } from 'mobx-react-lite';
import CountrySearchBox from '@/react/components/country/CountrySearchBox';
import { useLocale } from '@/react/hooks/useLocale';
import { MobileDrawer } from '../../MobileDrawer';
import { MobileNavItem } from '../../MobileNavItem';
import { NavigationLink } from '../../NavigationLink';

import S from './styles.base.module.scss';

/**
 * Country selector that redirects to the selected country page.
 */
export const ChooseCountryUtilityLink: FC = observer(
  function ChooseCountryUtilityLink() {
    const { country, language } = useLocale();
    const flagIconType = `${country}Flag` as keyof typeof IconTypes;
    const countryString = getCountryName(country);
    const languageString = getLanguageName(language);

    const label = `${countryString} | ${languageString}`;

    const vm = useMemo(() => new LocaleListVM(), []);

    return (
      <MobileDrawer
        labelClassName={S.utilityLink}
        label={label}
        icon={IconTypes[flagIconType]}
      >
        <ul className={S.nav}>
          <CountrySearchBox vm={vm} isMobileNav />
          {vm.filteredSites.map((item) => {
            const label = (
              <div className={S.countryLabel}>
                <span className={S.countryIcon}>
                  <Icon icon={item.icon} />
                </span>
                {item.name}
              </div>
            ) as ReactNode;

            return (
              <MobileNavItem key={item.id}>
                <NavigationLink
                  item={
                    {
                      id: item.countryID,
                      href: item.url.href,
                      name: label
                    } as NavigationModel
                  }
                />
              </MobileNavItem>
            );
          })}
          <MobileNavItem>
            <NavigationLink
              item={
                {
                  href: '/choose-country',
                  name: (<ViewMoreCountries />) as ReactNode,
                  id: 'choose-country'
                } as NavigationModel
              }
            />
          </MobileNavItem>
        </ul>
      </MobileDrawer>
    );
  }
);
